import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { PageHeader, Table, Badge, Btn } from "../components/common";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Plus, Pencil, Trash2, X, Key, History, Send } from "lucide-react";
import { overlayCloseHandlers } from "../lib/overlayClose";
import { useAutoRefresh } from "../lib/autoRefresh";

const EMPTY = { nome: "", email: "", password: "", roles: [], telegram_chat_id: "", send_invite: true };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [mfaMap, setMfaMap] = useState({});
  const [edit, setEdit] = useState(null); // { _new: true, ... } or user obj

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadMfa = () => api.get("/admin/mfa/users").then((r) => {
    const m = {};
    (r.data || []).forEach((u) => { m[u.id] = u; });
    setMfaMap(m);
  }).catch(() => {});

  useEffect(() => {
    load();
    loadMfa();
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  }, []);
  useAutoRefresh(load);

  const toggleRole = async (u, key) => {
    const cur = new Set(u.roles || []);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    try {
      await api.put(`/users/${u.id}/roles`, { roles: [...cur] });
      toast.success("Roles atualizados");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleForceMfa = async (u) => {
    const cur = !!mfaMap[u.id]?.mfa_forced;
    try {
      await api.patch(`/admin/mfa/users/${u.id}/force`, { forced: !cur });
      toast.success(!cur ? "MFA obrigatoria activada" : "MFA obrigatoria desactivada");
      loadMfa();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [mfaDebug, setMfaDebug] = useState(null);
  const debugMfa = async (u) => {
    try {
      const { data } = await api.get(`/auth/mfa/user-debug/${u.id}`);
      setMfaDebug(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async (u) => {
    if (u.id === me?.id) return toast.error("Nao pode eliminar-se a si proprio.");
    if (!window.confirm(`Eliminar definitivamente o utilizador ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilizador eliminado.");
      load(); loadMfa();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cols = [
    { key: "nome", label: "Nome", render: (r) => (
      <div>
        <div className="font-semibold text-navy">{r.nome}</div>
        {r.ativo === false && <span className="text-[10px] font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded">INACTIVO</span>}
      </div>
    ) },
    { key: "email", label: "Email" },
    {
      key: "mfa",
      label: "MFA",
      render: (u) => {
        const info = mfaMap[u.id] || {};
        return (
          <div className="flex items-center gap-1.5" data-testid={`mfa-cell-${u.id}`}>
            {info.mfa_enabled ? <Badge value="ON" tone="green" /> : <span className="text-[10px] text-muted2">off</span>}
            {info.mfa_forced && <Badge value="FORCADA" tone="orange" />}
            {info.telegram_configured && <span title="Telegram OTP configurado" className="text-[10px] text-emerald-700">TG</span>}
            <button onClick={() => debugMfa(u)} data-testid={`mfa-debug-${u.id}`}
              title="Diagnostico MFA (ver estado + ultimas tentativas)"
              className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-200 text-navy hover:bg-blue hover:text-white">
              Diag
            </button>
            <button onClick={() => toggleForceMfa(u)} data-testid={`mfa-force-${u.id}`}
              title={info.mfa_forced ? "Desforcar MFA" : "Forcar MFA"}
              className={`text-[10px] font-semibold px-2 py-0.5 rounded transition ${info.mfa_forced ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-tint text-navy hover:bg-blue hover:text-white"}`}>
              {info.mfa_forced ? <><ShieldOff size={10} className="inline" /> Desforcar</> : <><ShieldCheck size={10} className="inline" /> Forcar</>}
            </button>
          </div>
        );
      },
    },
    {
      key: "roles",
      label: "Roles (multi-role)",
      render: (u) => (
        <div className="flex gap-1 flex-wrap">
          {roles.map((role) => {
            const active = (u.roles || []).includes(role.key);
            return (
              <button key={role.key} onClick={() => toggleRole(u, role.key)}
                data-testid={`role-${u.id}-${role.key}`}
                className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-colors ${active ? "bg-blue text-white" : "bg-tint text-muted2 hover:bg-[#dceffb]"}`}>
                {role.label}
              </button>
            );
          })}
        </div>
      ),
    },
    {
      key: "acoes",
      label: "Acoes",
      render: (u) => (
        <div className="flex items-center gap-1">
          <button onClick={() => setEdit({ ...u, password: "" })} data-testid={`user-edit-${u.id}`}
            title="Editar" className="p-1 text-navy hover:bg-tint rounded"><Pencil size={13} /></button>
          <button onClick={() => doDelete(u)} disabled={u.id === me?.id}
            data-testid={`user-delete-${u.id}`} title={u.id === me?.id ? "Nao pode eliminar-se a si proprio" : "Eliminar"}
            className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed">
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        overline="Acessos"
        title="Utilizadores e RBAC"
        action={<Btn onClick={() => setEdit({ ...EMPTY, _new: true })} testid="user-new-btn"><Plus size={14} /> Novo utilizador</Btn>}
      />
      <p className="text-muted2 text-sm mb-6 max-w-2xl leading-relaxed">
        Um utilizador pode acumular varios roles. As permissoes efetivas sao a uniao das permissoes de todos os roles atribuidos. Clique num role para ativar ou desativar. A coluna MFA permite forcar autenticacao em dois passos (obrigatoria no proximo login).
      </p>
      <Table columns={cols} rows={users} testid="users-table" />

      {edit && <UserModal user={edit} roles={roles} onClose={() => setEdit(null)}
        onSaved={() => { setEdit(null); load(); loadMfa(); }} />}

      {mfaDebug && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" {...overlayCloseHandlers(() => setMfaDebug(null))}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mfa-debug-modal">
            <div className="flex items-center justify-between p-4 border-b border-tint">
              <h3 className="font-bold text-navy">Diagnostico MFA - {mfaDebug.email}</h3>
              <button onClick={() => setMfaDebug(null)} className="text-muted2 hover:text-navy"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 text-[12px]">
              <div className="grid grid-cols-2 gap-2">
                <div><b>MFA activa:</b> {mfaDebug.mfa_enabled ? "SIM" : "NAO"}</div>
                <div><b>MFA forcada:</b> {mfaDebug.mfa_forced ? "SIM" : "NAO"}</div>
                <div><b>Telegram chat_id:</b> {mfaDebug.has_telegram_chat_id ? mfaDebug.telegram_chat_id_hint : <span className="text-red-600">EM FALTA</span>}</div>
                <div><b>Codigos recuperacao:</b> {mfaDebug.recovery_codes_count}</div>
                <div><b>Bot token (sistema):</b> {mfaDebug.bot_token_configurado ? "OK" : <span className="text-red-600">EM FALTA</span>}</div>
                <div><b>Telegram enabled:</b> {mfaDebug.telegram_enabled ? "SIM" : "NAO"}</div>
              </div>
              <div className="border-t border-tint pt-3">
                <div className="font-semibold text-navy mb-2">Ultimas 5 tentativas OTP:</div>
                {(!mfaDebug.recent_otp_attempts || mfaDebug.recent_otp_attempts.length === 0) ? (
                  <div className="text-muted2 italic text-[11px]">Nenhuma tentativa registada.</div>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead className="text-slate-500 uppercase text-[10px] border-b border-tint">
                      <tr><th className="text-left py-1">Quando</th><th className="text-left">Etapa</th><th className="text-center">OK?</th><th className="text-left">Erro</th></tr>
                    </thead>
                    <tbody>
                      {mfaDebug.recent_otp_attempts.map((a, i) => (
                        <tr key={i} className="border-b border-tint last:border-b-0">
                          <td className="py-1">{(a.em || "").slice(0, 19).replace("T", " ")}</td>
                          <td>{a.etapa}</td>
                          <td className="text-center">{a.ok ? "OK" : "X"}</td>
                          <td className="text-red-600 truncate max-w-xs" title={a.erro || ""}>{a.erro || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function UserModal({ user, roles, onClose, onSaved }) {
  const isNew = !!user._new;
  const [form, setForm] = useState({
    nome: user.nome || "", email: user.email || "",
    password: user.password || "", telegram_chat_id: user.telegram_chat_id || "",
    letscall_extension: user.letscall_extension || "",
    ativo: user.ativo !== false, roles: user.roles || [],
    send_invite: user.send_invite ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);

  const toggleRole = (key) => {
    const cur = new Set(form.roles || []);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    setForm({ ...form, roles: [...cur] });
  };

  const sendReset = async () => {
    if (isNew) return toast.error("Guarde o utilizador primeiro.");
    if (!window.confirm(`Enviar link de reset de password para ${form.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${user.id}/password-reset`);
      const link = data?.reset_link || "";
      if (data?.enviado) {
        toast.success("Link de reset enviado por email.");
      } else if (link) {
        try { await navigator.clipboard.writeText(link); } catch (_) {}
        window.prompt("Email nao enviado (SendGrid nao configurado ou falhou). Copie o link abaixo e envie manualmente:", link);
      } else {
        toast.success(data?.message || "Pedido processado.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadHistory = async () => {
    setShowHistory(true); setHistory(null);
    try {
      const { data } = await api.get(`/users/${user.id}/history`);
      setHistory(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Indique o nome.");
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Email invalido.");
    if (isNew && !form.send_invite && (!form.password || form.password.length < 10)) return toast.error("Password minima 10 caracteres OU active envio de convite.");
    if (!isNew && form.password && form.password.length < 10) return toast.error("Password minima 10 caracteres.");
    setBusy(true);
    try {
      if (isNew) {
        // Se envio de convite activo, gera password temporaria aleatoria
        let pwd = form.password;
        if (form.send_invite && !pwd) {
          pwd = "Tmp" + Math.random().toString(36).slice(2, 10) + "!" + Math.random().toString(36).slice(2, 5);
        }
        const { data } = await api.post("/users", {
          nome: form.nome, email: form.email, password: pwd,
          roles: form.roles, telegram_chat_id: form.telegram_chat_id,
          send_invite: !!form.send_invite,
        });
        if (form.send_invite) {
          const link = data?.invite_link || "";
          if (data?.invite_enviado) {
            toast.success("Utilizador criado e convite enviado por email.");
          } else if (link) {
            try { await navigator.clipboard.writeText(link); } catch (_) {}
            window.prompt("Utilizador criado. Email nao enviado (SendGrid nao configurado ou falhou). Copie o link e envie manualmente:", link);
          } else {
            toast.success("Utilizador criado.");
          }
        } else {
          toast.success("Utilizador criado.");
        }
      } else {
        const body = { nome: form.nome, email: form.email, ativo: form.ativo,
          telegram_chat_id: form.telegram_chat_id,
          letscall_extension: form.letscall_extension };
        if (form.password) body.password = form.password;
        await api.put(`/users/${user.id}`, body);
        const curSet = new Set(user.roles || []);
        const newSet = new Set(form.roles || []);
        const changed = curSet.size !== newSet.size || [...curSet].some((r) => !newSet.has(r));
        if (changed) {
          await api.put(`/users/${user.id}/roles`, { roles: form.roles });
        }
        toast.success("Utilizador atualizado.");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="user-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-tint">
          <h2 className="text-lg font-bold text-navy">{isNew ? "Novo utilizador" : `Editar ${user.nome}`}</h2>
          <button onClick={onClose} className="text-muted2 hover:text-navy" data-testid="user-modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Nome</label>
            <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
              data-testid="user-nome" className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-email" className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">
              Password {isNew ? (form.send_invite ? "(opcional - convite enviado)" : "(minimo 10 caracteres)") : "(deixe vazio para manter)"}
            </label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="user-password" autoComplete="new-password"
              placeholder={isNew ? (form.send_invite ? "Deixe vazio - sera enviado convite" : "Ex: Utilizador2026Seguro") : "Nova password (opcional)"}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Telegram chat_id (opcional, para failsafe MFA)</label>
            <input value={form.telegram_chat_id} onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
              data-testid="user-telegram-chat-id" placeholder="ex: 123456789"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm font-mono" />
            <p className="text-[10px] text-muted2 mt-0.5">Se preenchido, o utilizador pode receber OTP por Telegram no login MFA.</p>
          </div>
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Extensao LetsCall One (opcional)</label>
            <input value={form.letscall_extension} onChange={(e) => setForm({ ...form, letscall_extension: e.target.value })}
              data-testid="user-letscall-extension" placeholder="ex: 201"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm font-mono" />
            <p className="text-[10px] text-muted2 mt-0.5">Extensao usada automaticamente ao clicar em "Ligar" no CRM. Cada utilizador usa a sua propria extensao.</p>
          </div>
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Roles</label>
            <div className="flex gap-1 flex-wrap">
              {roles.map((r) => {
                const active = (form.roles || []).includes(r.key);
                return (
                  <button key={r.key} type="button" onClick={() => toggleRole(r.key)}
                    data-testid={`user-modal-role-${r.key}`}
                    className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-colors ${active ? "bg-blue text-white" : "bg-tint text-muted2 hover:bg-[#dceffb]"}`}>
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
          {isNew && (
            <label className="flex items-start gap-2 text-sm text-navy cursor-pointer py-2 px-3 bg-blue/5 border border-blue/20 rounded-md">
              <input type="checkbox" checked={!!form.send_invite}
                onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                data-testid="user-send-invite" className="mt-0.5 rounded border-slate-300" />
              <span className="flex-1">
                <b>Enviar convite por email</b>
                <span className="block text-[11px] text-slate-600 mt-0.5">
                  Sera gerada uma password temporaria aleatoria e enviado um email com link de first-login (30 min) para o novo utilizador definir a sua propria password.
                </span>
              </span>
            </label>
          )}
          {!isNew && (
            <label className="flex items-center gap-2 text-sm text-navy cursor-pointer pt-1">
              <input type="checkbox" checked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                data-testid="user-ativo" className="rounded border-slate-300" />
              Utilizador activo (pode iniciar sessao)
            </label>
          )}
        </div>
        {!isNew && (
          <div className="px-4 py-3 border-t border-tint bg-slate-50 flex flex-wrap gap-2 items-center" data-testid="user-modal-tools">
            <span className="text-[11px] uppercase text-muted2 font-semibold mr-2">Ferramentas</span>
            <Btn variant="ghost" onClick={sendReset} testid="user-reset-password" size="sm">
              <Key size={12} /> Enviar link de reset
            </Btn>
            <Btn variant="ghost" onClick={loadHistory} testid="user-history-btn" size="sm">
              <History size={12} /> Ver actividade
            </Btn>
          </div>
        )}
        <div className="flex justify-end gap-2 p-4 border-t border-tint">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={busy} testid="user-save">{busy ? "A guardar..." : "Guardar"}</Btn>
        </div>
      </div>
      {showHistory && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" {...overlayCloseHandlers(() => setShowHistory(false))}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="user-history-modal">
            <div className="flex items-center justify-between p-4 border-b border-tint">
              <h3 className="font-bold text-navy">Actividade de {history?.user?.nome || user.nome}</h3>
              <button onClick={() => setShowHistory(false)} className="text-muted2 hover:text-navy"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {!history && <p className="text-sm text-muted2">A carregar...</p>}
              {history && (history.events || []).length === 0 && <p className="text-sm text-muted2 italic">Sem eventos registados.</p>}
              {history && (history.events || []).length > 0 && (
                <ul className="space-y-2" data-testid="user-history-list">
                  {history.events.map((ev, i) => (
                    <li key={i} className="border border-tint rounded-md px-3 py-2 text-[13px] flex items-start gap-3">
                      <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${ev.tipo === "login_ok" ? "bg-emerald-500" : ev.tipo === "login_falhou" ? "bg-red-500" : ev.tipo.startsWith("mfa:") ? "bg-blue" : "bg-slate-400"}`}></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-navy">{ev.tipo}</div>
                        <div className="text-[11px] text-slate-500">
                          {ev.at ? new Date(ev.at).toLocaleString("pt-PT") : "-"}
                          {ev.meta?.autor && <span className="ml-2">por {ev.meta.autor}</span>}
                          {ev.meta?.ip_hash && <span className="ml-2 font-mono">ip {ev.meta.ip_hash}</span>}
                        </div>
                        {ev.meta?.detalhes && Object.keys(ev.meta.detalhes).length > 0 && (
                          <pre className="mt-1 text-[10px] bg-slate-50 p-1.5 rounded overflow-x-auto">{JSON.stringify(ev.meta.detalhes, null, 0)}</pre>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
